import { useState } from 'react';
import { ChevronRight, ChevronDown, Lightbulb } from 'lucide-react';

interface AnalysisFormProps {
  claims: string;
  argument: string;
  theory: string;
  onClaimsChange: (value: string) => void;
  onArgumentChange: (value: string) => void;
  onTheoryChange: (value: string) => void;
  minAnnotations: number;
  currentAnnotations: number;
  readonly?: boolean;
}

const PROMPTS = {
  claims: [
    "What patterns do you notice in how the AI expresses certainty?",
    "Which claims lack supporting evidence?",
    "What hedging language appears, and what might it hide?"
  ],
  argument: [
    "How do your individual observations connect?",
    "What strategy does the AI seem to be using?",
    "Why might the AI have structured its response this way?"
  ],
  theory: [
    "What does this reveal about AI limitations in general?",
    "How might these patterns mislead users?",
    "What should AI-literate readers watch for?"
  ]
};

export function AnalysisForm({
  claims,
  argument,
  theory,
  onClaimsChange,
  onArgumentChange,
  onTheoryChange,
  minAnnotations,
  currentAnnotations,
  readonly = false
}: AnalysisFormProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>('claims');
  const canProceed = currentAnnotations >= minAnnotations;

  const Section = ({
    id,
    title,
    description,
    value,
    onChange,
    placeholder,
    minChars = 100
  }: {
    id: string;
    title: string;
    description: string;
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    minChars?: number;
  }) => {
    const isExpanded = expandedSection === id;
    const charCount = value.length;
    const isComplete = charCount >= minChars;
    const prompts = PROMPTS[id as keyof typeof PROMPTS] || [];

    return (
      <div className="border border-gray-700 rounded-lg overflow-hidden">
        <button
          onClick={() => setExpandedSection(isExpanded ? null : id)}
          className="w-full flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-750 transition-colors"
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
            <div className="text-left">
              <h3 className="font-medium text-gray-200">{title}</h3>
              <p className="text-sm text-gray-500">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isComplete && (
              <span className="text-xs bg-green-600/20 text-green-400 px-2 py-0.5 rounded">
                Complete
              </span>
            )}
            <span className="text-xs text-gray-500">
              {charCount}/{minChars}+
            </span>
          </div>
        </button>

        {isExpanded && (
          <div className="p-4 bg-gray-850 space-y-3">
            {!readonly && prompts.length > 0 && (
              <div className="flex items-start gap-2 p-3 bg-gray-800 rounded border border-gray-700">
                <Lightbulb className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
                <div className="text-sm text-gray-400">
                  <span className="font-medium text-gray-300">Consider:</span>
                  <ul className="mt-1 space-y-1">
                    {prompts.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              disabled={readonly}
              className="w-full h-40 bg-gray-900 border border-gray-700 rounded-lg p-3 text-gray-200 placeholder-gray-500 resize-none focus:outline-none focus:border-blue-500 disabled:opacity-60"
            />
          </div>
        )}
      </div>
    );
  };

  if (!canProceed) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg text-center">
        <p className="text-gray-400">
          Complete at least <span className="text-blue-400 font-medium">{minAnnotations}</span> annotations
          before proceeding to analysis.
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Current: {currentAnnotations} / {minAnnotations}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Section
        id="claims"
        title="Local Claims"
        description="3-5 bullet points based on your annotations"
        value={claims}
        onChange={onClaimsChange}
        placeholder="Based on my annotations, I notice that...&#10;&#10;• [First observation]&#10;• [Second observation]&#10;• [Third observation]"
        minChars={100}
      />

      <Section
        id="argument"
        title="Regional Argument"
        description="1-2 paragraphs connecting your claims"
        value={argument}
        onChange={onArgumentChange}
        placeholder="These patterns suggest that...&#10;&#10;Looking at the annotations together, I see..."
        minChars={200}
      />

      <Section
        id="theory"
        title="Global Theory"
        description="What this reveals about AI more broadly"
        value={theory}
        onChange={onTheoryChange}
        placeholder="This analysis reveals something broader about AI...&#10;&#10;Users should be aware that..."
        minChars={150}
      />
    </div>
  );
}

export default AnalysisForm;
